import type { DownloadManagerItem } from '/#/components/base/downloads'

import { useStorage } from '@vueuse/core'
import set from 'lodash-es/set'
import { defineStore } from 'pinia'

import { FileDownloadStatusEnum } from '/@/enums/fileDownloadStatusEnum'
import { store } from '/@/store'

export const useDownloadsStore = defineStore('downloads', () => {
  /**
   * @var downloads {}
   * @description Holds downloded files.
   */
  const downloads = useStorage<DownloadManagerItem[]>('downloads', [])

  function updateDownloadProgress(id: string, downloadedSize: number) {
    const file = downloads.value.find((file) => file.id === id)
    const progress = Math.min(
      Math.floor((downloadedSize / (file?.totalSize || 0)) * 100),
      100,
    )
    if (file) {
      set(file, 'downloadedSize', downloadedSize)
      set(file, 'progress', progress)
      if (progress < 0) {
        set(file, 'status', FileDownloadStatusEnum.FAILED)
      } else if (progress === 100) {
        set(file, 'status', FileDownloadStatusEnum.COMPLETED)
        // TODO: totalSize unknown until backend send 'Content-length' for fe!
        set(file, 'totalSize', downloadedSize)
      } else {
        set(file, 'status', FileDownloadStatusEnum.IN_PROGRESS)
      }
    }
  }

  function clearDownloads() {
    downloads.value = []
  }

  function addDownloadManager(file: DownloadManagerItem) {
    downloads.value.push(file)
  }

  return {
    downloads,
    addDownloadManager,
    updateDownloadProgress,
    clearDownloads,
  }
})

// Need to be used outside the setup
export function useDownloadManagerStoreWithout() {
  return useActivityStore(store)
}

/**
 * Pinia supports Hot Module replacement so you can edit your store and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDownloadsStore, import.meta.hot))
}
